import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router/dist'
import { RouteTypes } from '~/routes/constants'
import { useDebounce } from 'use-lodash-debounce'
import { useTranslation } from 'react-i18next'
import i18n from '~/components/i18n'
import RegistrationViewModal from './RegistrationViewModal'
import styled from 'styled-components'
import { Dropdown } from 'semantic-ui-react'
import TableComponent from '~/components/TableComponent'
import { El } from '~/components/Layout/style'
import { ClimaProgramQuery } from '~/QueryHooks/ClimaProgramQuery'

const FiltersWrapper = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
`

const TabActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`

const getTableColumns = () => [
  {
    name: i18n.t('EVS Nr.'),
    key: 'evsNumber'
  },
  {
    name: i18n.t('AGIS-ID Operation'),
    key: 'agisId'
  },
  {
    name: i18n.t('Registration date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName'
  },
  {
    name: i18n.t('Street'),
    key: 'streetName',
    type: 'street'
  },
  {
    name: i18n.t('Zip'),
    key: 'zip'
  },
  {
    name: i18n.t('City'),
    key: 'city'
  },
  {
    name: i18n.t('Actions')
  }
]

const RegistrationsTab: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()
  const actions = [
    {
      popupMessage: t('Registration details'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionType: 'view'
    }
  ]

  const [registrationId, setRegistrationId] = useState(null)
  const [farmerSelectedId, setFarmerSelectedId] = useState('')
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    year: currentYear,
    considerationYear: currentYear,
    search: ''
  })
  const [search, setSearch] = useState('')
  const debouncedSearchValue = useDebounce(search, 300)
  const tableColumnHeaders = getTableColumns()

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const year = currentYear - i
    return { key: year, value: year, text: year.toString() }
  })

  const {
    data: registrationData,
    isLoading: isLoadingRegData
  } = ClimaProgramQuery.getClimaRegOptionsData()

  const {
    data: registrations,
    isLoading: isLoadingRegistrations,
    isFetching: isFetchingReg,
    refetch: fetchRegistrations
  } = ClimaProgramQuery.getClimaRegistrationsIndex({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  useEffect(() => {
    if (registrationData && !farmerSelectedId) {
      const hashId = location.hash.replace('#', '')
      setFarmerSelectedId(hashId || registrationData?.farmers[0]?.id)
    }
  }, [registrationData, farmerSelectedId])

  useEffect(() => {
    fetchRegistrations()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 })
  }, [debouncedSearchValue])

  function handleActionClick(row, type) {
    switch (type) {
      case 'view':
        return setRegistrationId(row?.id)
      default:
        return
    }
  }

  const { filtered, rows } = registrations ? registrations : { filtered: 0, rows: [] }

  const farmerRegistered = registrationData?.farmers.find(farmer => farmer.id === farmerSelectedId)?.climateProgramRegistration

  return (
    <>
      <TabActionsWrapper>
        <FiltersWrapper>
          <Dropdown
            selection
            selectOnBlur={false}
            value={dataFiltered.year}
            options={yearOptions}
            onChange={(_, data) => setDataFiltered({ ...dataFiltered, year: data.value as number, page: 0 })}
          />
        </FiltersWrapper>
        <FiltersWrapper>
          {registrationData?.farmers?.length > 0 && <Dropdown
            selection
            selectOnBlur={false}
            value={farmerSelectedId}
            options={registrationData?.farmers?.map(farmer => ({ value: farmer.id, text: farmer.companyName }))}
            onChange={(_, data) => setFarmerSelectedId(data.value as string)}
          />}
          {Boolean(farmerSelectedId) && !farmerRegistered && <El.PageHeaderActionButton
            onClick={() => navigate(RouteTypes.MANAGER_PROFILE_REGISTRATION_ADD.replace(':id', farmerSelectedId))}
          >
            {t('Register')}
          </El.PageHeaderActionButton>}
        </FiltersWrapper>
      </TabActionsWrapper>
      <RegistrationViewModal
        registrationId={registrationId}
        onClose={() => setRegistrationId(null)}
      />
      <TableComponent
        isFetchingData={isFetchingReg || isLoadingRegData || isLoadingRegistrations}
        animated
        actions={actions}
        tableColumnHeaders={tableColumnHeaders}
        handleClickAction={(row, type) => handleActionClick(row, type)}
        dataFiltered={dataFiltered}
        handleChangeDataFiltered={(data) => setDataFiltered(data)}
        rows={rows}
        total={filtered}
      />
    </>
  )
}

export default RegistrationsTab
